import React, { useState, useMemo, useEffect } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import Swimlane from './Swimlane';
import Sidebar from './Sidebar';
import { BoardData } from '../types/board';
import { fetchBoardData, updateCard, deleteCard as deleteCardFromDb, fetchOwners } from '../services/supabase';
import './Board.css';

type SwimlaneMode = 'site' | 'owner' | 'date';

const Board: React.FC = () => {
  const [boardData, setBoardData] = useState<BoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [swimlaneMode, setSwimlaneMode] = useState<SwimlaneMode>('site');
  const [hiddenLabelIds, setHiddenLabelIds] = useState<string[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null);
  const [owners, setOwners] = useState<string[]>([]);

  // Load board data from Supabase on mount
  useEffect(() => {
    loadBoardData();
    loadOwners();
  }, []);

  const loadOwners = async () => {
    const ownersData = await fetchOwners();
    setOwners(ownersData);
  };

  const loadBoardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBoardData();
      setBoardData(data);
    } catch (err) {
      console.error('Failed to load board data:', err);
      setError('Failed to load board data. Please check your Supabase configuration.');
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!boardData) return;

    const { destination, source, draggableId } = result;

    // Prevent moving WordPress cards (read-only)
    const draggedCard = boardData.cards[draggableId];
    if (draggedCard?.wordpressPostId) {
      console.log('WordPress cards cannot be moved between columns. Card state is synced from WordPress.');
      return;
    }

    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Parse droppableIds: format is "labelId-columnId"
    // Column IDs start with "column-", so we can safely split on that pattern
    const parseDroppableId = (droppableId: string) => {
      const match = droppableId.match(/^(.+)-(column-.+)$/);
      if (match) {
        return [match[1], match[2]];
      }
      // Fallback: assume last segment after dash is column
      const lastDashIndex = droppableId.lastIndexOf('-');
      return [
        droppableId.substring(0, lastDashIndex),
        droppableId.substring(lastDashIndex + 1)
      ];
    };

    const [sourceLabelId, sourceColumnId] = parseDroppableId(source.droppableId);
    const [destLabelId, destColumnId] = parseDroppableId(destination.droppableId);

    const card = boardData.cards[draggableId];
    const newCards = { ...boardData.cards };
    let newLabelIds = [...card.labelIds];

    // Update card's labels if moving between swimlanes
    if (sourceLabelId !== destLabelId) {
      newLabelIds = card.labelIds.filter((id) => id !== sourceLabelId);
      if (!newLabelIds.includes(destLabelId)) {
        newLabelIds.push(destLabelId);
      }
      newCards[draggableId] = { ...card, labelIds: newLabelIds };
    }

    // Update column cardIds if moving between columns
    if (sourceColumnId !== destColumnId) {
      const sourceColumn = boardData.columns[sourceColumnId];
      const destColumn = boardData.columns[destColumnId];

      const newSourceCardIds = Array.from(sourceColumn.cardIds);
      newSourceCardIds.splice(
        newSourceCardIds.findIndex((id) => id === draggableId),
        1
      );

      const newDestCardIds = Array.from(destColumn.cardIds);
      // Find the correct index in the destination column
      const destCards = newDestCardIds
        .map((id) => boardData.cards[id])
        .filter((c) => c && c.labelIds.includes(destLabelId));
      newDestCardIds.splice(
        newDestCardIds.indexOf(destCards[destination.index]?.id) >= 0
          ? newDestCardIds.indexOf(destCards[destination.index].id)
          : newDestCardIds.length,
        0,
        draggableId
      );

      // Optimistic UI update
      setBoardData({
        ...boardData,
        cards: newCards,
        columns: {
          ...boardData.columns,
          [sourceColumnId]: { ...sourceColumn, cardIds: newSourceCardIds },
          [destColumnId]: { ...destColumn, cardIds: newDestCardIds },
        },
      });

      // Update database
      try {
        await updateCard(
          draggableId,
          { labelIds: newLabelIds },
          destColumnId
        );
      } catch (err) {
        console.error('Failed to update card:', err);
        // Reload data on error
        loadBoardData();
      }
    } else {
      // Same column, just update cards
      setBoardData({
        ...boardData,
        cards: newCards,
      });

      // Update database if labels changed
      if (sourceLabelId !== destLabelId) {
        try {
          await updateCard(draggableId, { labelIds: newLabelIds });
        } catch (err) {
          console.error('Failed to update card labels:', err);
          loadBoardData();
        }
      }
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!boardData) return;

    const newCards = { ...boardData.cards };
    delete newCards[cardId];

    const newColumns = { ...boardData.columns };
    Object.keys(newColumns).forEach((columnId) => {
      newColumns[columnId] = {
        ...newColumns[columnId],
        cardIds: newColumns[columnId].cardIds.filter((id) => id !== cardId),
      };
    });

    // Optimistic UI update
    setBoardData({
      ...boardData,
      cards: newCards,
      columns: newColumns,
    });

    // Delete from database
    try {
      await deleteCardFromDb(cardId);
    } catch (err) {
      console.error('Failed to delete card:', err);
      // Reload data on error
      loadBoardData();
    }
  };

  const handleUpdateOwner = async (cardId: string, newOwner: string) => {
    if (!boardData) return;

    const card = boardData.cards[cardId];
    const updatedCard = { ...card, owner: newOwner || undefined };

    // Optimistic UI update
    setBoardData({
      ...boardData,
      cards: {
        ...boardData.cards,
        [cardId]: updatedCard,
      },
    });

    // Update database
    try {
      await updateCard(cardId, { owner: newOwner || undefined });
    } catch (err) {
      console.error('Failed to update card owner:', err);
      // Reload data on error
      loadBoardData();
    }
  };

  const handleUpdateDueDate = async (cardId: string, newDueDate: string) => {
    if (!boardData) return;

    const card = boardData.cards[cardId];
    const updatedCard = { ...card, dueDate: newDueDate || undefined };

    // Optimistic UI update
    setBoardData({
      ...boardData,
      cards: {
        ...boardData.cards,
        [cardId]: updatedCard,
      },
    });

    // Update database
    try {
      await updateCard(cardId, { dueDate: newDueDate || undefined });
    } catch (err) {
      console.error('Failed to update card due date:', err);
      // Reload data on error
      loadBoardData();
    }
  };

  const handleUpdateLocalTitle = async (cardId: string, newLocalTitle: string) => {
    if (!boardData) return;

    const card = boardData.cards[cardId];
    const updatedCard = { ...card, localTitle: newLocalTitle || undefined };

    // Optimistic UI update
    setBoardData({
      ...boardData,
      cards: {
        ...boardData.cards,
        [cardId]: updatedCard,
      },
    });

    // Update database
    try {
      await updateCard(cardId, { localTitle: newLocalTitle || undefined });
    } catch (err) {
      console.error('Failed to update card local title:', err);
      // Reload data on error
      loadBoardData();
    }
  };

  const handleUpdateLocalPlan = async (cardId: string, newLocalPlan: string) => {
    if (!boardData) return;

    const card = boardData.cards[cardId];
    const updatedCard = { ...card, localPlan: newLocalPlan || undefined };

    // Optimistic UI update
    setBoardData({
      ...boardData,
      cards: {
        ...boardData.cards,
        [cardId]: updatedCard,
      },
    });

    // Update database
    try {
      await updateCard(cardId, { localPlan: newLocalPlan || undefined });
    } catch (err) {
      console.error('Failed to update card local plan:', err);
      // Reload data on error
      loadBoardData();
    }
  };

  const toggleLabelVisibility = (labelId: string) => {
    setHiddenLabelIds((prev) =>
      prev.includes(labelId)
        ? prev.filter((id) => id !== labelId)
        : [...prev, labelId]
    );
  };

  // Calculate card counts by label
  const cardCountsByLabel = useMemo(() => {
    if (!boardData) return {};
    const counts: { [key: string]: number } = {};
    Object.values(boardData.cards).forEach((card) => {
      card.labelIds.forEach((labelId) => {
        counts[labelId] = (counts[labelId] || 0) + 1;
      });
    });
    return counts;
  }, [boardData]);

  // Get unique owners - combine predefined list with any existing owners

  // Calculate card counts by owner
  const cardCountsByOwner = useMemo(() => {
    if (!boardData) return {};
    const counts: { [key: string]: number } = {};
    Object.values(boardData.cards).forEach((card) => {
      if (card.owner) {
        counts[card.owner] = (counts[card.owner] || 0) + 1;
      }
    });
    return counts;
  }, [boardData]);

  // Get visible labels (not hidden)
  const visibleLabels = useMemo(() => {
    if (!boardData) return [];
    return Object.values(boardData.labels).filter(
      (label) => !hiddenLabelIds.includes(label.id)
    );
  }, [boardData, hiddenLabelIds]);

  // Generate swimlanes based on mode
  const swimlanes = useMemo(() => {
    if (swimlaneMode === 'site') {
      return visibleLabels.map((label) => ({
        id: label.id,
        name: label.name,
        color: label.color,
        type: 'site' as const,
      }));
    } else if (swimlaneMode === 'owner') {
      return owners.map((owner) => ({
        id: `owner-${owner}`,
        name: owner,
        color: '#6b778c',
        type: 'owner' as const,
      }));
    } else {
      // Date mode
      return [
        { id: 'date-overdue', name: 'Overdue', color: '#de350b', type: 'date' as const },
        { id: 'date-today', name: 'Today', color: '#ff991f', type: 'date' as const },
        { id: 'date-thisweek', name: 'This Week', color: '#0052cc', type: 'date' as const },
        { id: 'date-later', name: 'Later', color: '#006644', type: 'date' as const },
        { id: 'date-nodate', name: 'No Date', color: '#5e6c84', type: 'date' as const },
      ];
    }
  }, [swimlaneMode, visibleLabels, owners]);

  // Show loading state
  if (loading) {
    return (
      <div className="board-container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'white' }}>
          <p>Loading board data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !boardData) {
    return (
      <div className="board-container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'white', flexDirection: 'column', gap: '20px' }}>
          <p>{error || 'Failed to load board data'}</p>
          <button
            onClick={loadBoardData}
            style={{ padding: '10px 20px', cursor: 'pointer', borderRadius: '6px', border: 'none', background: 'white', fontWeight: 600 }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="board-container">
      <Sidebar
        swimlaneMode={swimlaneMode}
        labels={boardData.labels}
        hiddenLabelIds={hiddenLabelIds}
        onToggleLabel={toggleLabelVisibility}
        cardCountsByLabel={cardCountsByLabel}
        owners={owners}
        selectedOwner={selectedOwner}
        onSelectOwner={setSelectedOwner}
        cardCountsByOwner={cardCountsByOwner}
        selectedSite={selectedSite}
        onSelectSite={setSelectedSite}
        selectedDateRange={selectedDateRange}
        onSelectDateRange={setSelectedDateRange}
        onOwnersChanged={loadOwners}
      />
      <div className="board-content">
        <div className="board-header">
          <div className="board-header-left">
            <div className="swimlane-mode-selector">
              <label htmlFor="swimlane-mode">Group by:</label>
              <select
                id="swimlane-mode"
                value={swimlaneMode}
                onChange={(e) => setSwimlaneMode(e.target.value as SwimlaneMode)}
              >
                <option value="site">Site</option>
                <option value="owner">Owner</option>
                <option value="date">Due Date</option>
              </select>
            </div>
          </div>
          <div className="board-header-columns">
            {boardData.columnOrder.map((columnId) => (
              <div key={columnId} className="board-header-column">
                {boardData.columns[columnId].title}
              </div>
            ))}
          </div>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="board-swimlanes">
            {swimlanes.map((swimlane) => (
              <Swimlane
                key={swimlane.id}
                label={swimlane}
                columns={boardData.columns}
                columnOrder={boardData.columnOrder}
                cards={boardData.cards}
                labels={boardData.labels}
                selectedOwner={selectedOwner}
                selectedSite={selectedSite}
                selectedDateRange={selectedDateRange}
                swimlaneMode={swimlaneMode}
                onDeleteCard={handleDeleteCard}
                owners={owners}
                onUpdateOwner={handleUpdateOwner}
                onUpdateDueDate={handleUpdateDueDate}
                onUpdateLocalTitle={handleUpdateLocalTitle}
                onUpdateLocalPlan={handleUpdateLocalPlan}
                onSyncComplete={loadBoardData}
              />
            ))}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
};

export default Board;
